/**
 * Assigns `checked` through the element's own setter rather than replacing the property.
 *
 * `Object.defineProperty` would install an own data property that shadows the accessor permanently,
 * so the element stops reporting its real state and every later read returns the first value written
 * here — a Switch turns on and can then never turn off. `value` is deliberately still overwritten
 * below: components like FileUpload put a serialized payload there that no native setter would accept.
 */
function setNativeChecked(target: object, checked: boolean) {
  const descriptor =
    Object.getOwnPropertyDescriptor(target, 'checked') ??
    Object.getOwnPropertyDescriptor(Object.getPrototypeOf(target), 'checked');
  if (descriptor?.set) {
    descriptor.set.call(target, checked);
    return;
  }
  // Not a real input (e.g. a plain stub in a test) — a direct assignment is all that's available.
  (target as Record<string, unknown>).checked = checked;
}

/**
 * Creates a synthetic change event that mimics a native React change event.
 * Used for components that don't have a native input element (e.g., Checkbox, RadioGroup, Select).
 *
 * This enables React Hook Form compatibility via the `{...register('fieldName')}` pattern.
 */
export function createSyntheticChangeEvent<
  T extends HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement,
>(target: T | null, value: string, checked?: boolean): React.ChangeEvent<T> {
  // Create a minimal target object if no real element exists
  const syntheticTarget =
    target ??
    ({
      value,
      checked,
      name: '',
      type: checked !== undefined ? 'checkbox' : 'text',
    } as unknown as T);

  // Update target properties
  if (target) {
    // For real elements, we need to set the value without triggering a re-render
    Object.defineProperty(target, 'value', {
      writable: true,
      value,
    });
    if (checked !== undefined) {
      setNativeChecked(target, checked);
    }
  }

  const event: React.ChangeEvent<T> = {
    target: syntheticTarget,
    currentTarget: syntheticTarget,
    bubbles: true,
    cancelable: false,
    defaultPrevented: false,
    eventPhase: 0,
    isTrusted: false,
    nativeEvent: new Event('change', { bubbles: true }),
    preventDefault: () => {
      /* no-op */
    },
    stopPropagation: () => {
      /* no-op */
    },
    isPropagationStopped: () => false,
    isDefaultPrevented: () => false,
    persist: () => {
      /* no-op */
    },
    timeStamp: Date.now(),
    type: 'change',
  };

  return event;
}

/**
 * Creates a synthetic change event from a hidden input reference.
 * This is the preferred method when the component has a hidden input element.
 */
export function createSyntheticEventFromRef<T extends HTMLInputElement>(
  inputRef: React.RefObject<T | null>,
  value: string,
  checked?: boolean
): React.ChangeEvent<T> {
  const target = inputRef.current;
  return createSyntheticChangeEvent(target, value, checked);
}
